import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar.jsx";
import { supabase } from "../utils/supabaseClient.jsx";
import { translations } from '../utils/reservation.translate.js'
import { useLanguage } from '../components/LanguajeContext.jsx';
import {v4 as uuid} from 'uuid'
import defaultAvatar from "../assets/default-avatar.png";
import {
  FaCalendarAlt,
  FaClock,
  FaPhone,
  FaEnvelope,
  FaCheckCircle,
  FaUser,
  FaMoneyBillWave,
  FaKey,
} from "react-icons/fa";

const URL = process.env.REACT_APP_SERVER_URL;

async function getSignedImageUrl(path) {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from(process.env.REACT_APP_SUPABASE_PICTURE)
    .createSignedUrl(path, 60 * 60);
  if (error) {
    console.error("Error al obtener URL firmada:", error.message);
    return null;
  }
  return data.signedUrl;
}

function dateFromYMDLocal(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const Reservation = () => {
  const { language } = useLanguage(); 
  const t = translations[language]; 
  
  const { id_service } = useParams();
  const navigate = useNavigate();

  const [service, setService] = useState(null);
  const [operator, setOperator] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [participants, setParticipants] = useState(1);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [clientData, setClientData] = useState({
    name: "",
    email: "",
    phone: "",
    country: language === 'es' ? "Costa Rica" : "Costa Rica",
  });

  const [currency, setCurrency] = useState("CRC");
  const [paymentOption, setPaymentOption] = useState("CARD");
  const [isPaying, setIsPaying] = useState(false);
  
  // Nuevos estados para la verificación
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [verificationError, setVerificationError] = useState("");


  useEffect(() => {
    
    const fetchServiceData = async () => {
      try {
        const response = await fetch(`${URL}/api/booking/${id_service}`);
        const data = await response.json();

        if (data.message) {
          const serviceData = data.message;
          setService({
            id_service: serviceData.id_service,
            name: serviceData.name,
            price: serviceData.price,
            description: serviceData.description,
            category: serviceData.category,
            max_capacity: serviceData.max_capacity,
            coverImage: serviceData.url_image?? "",
            
          });

          setOperator({
            id_operator: serviceData.operator.id_operator,
            Name: serviceData.operator.name,
            Last_name1: serviceData.operator.last_name1,
            Last_name2: serviceData.operator.last_name2,
            Email: serviceData.operator.email,
            Phone: serviceData.operator.phone,
            Description: serviceData.operator.description,
            Picture: serviceData.operator.picture || defaultAvatar,
            Location: "La Fortuna, Alajuela",
            Status: serviceData.operator.status,
          });
          setAvailability(
            serviceData.available.map((avail, index) => ({
              id_available:avail.id_available,
              date_service: avail.date_service,
              hour_start: avail.hour_start,
              hour_end: avail.hour_end,
              slots: serviceData.max_capacity,
            }))
          );

          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching service data:", error);
        setLoading(false);
      }
    };

    fetchServiceData();
  }, [language, id_service]);

  const handleDateSelect = (id) => setSelectedDate(id === selectedDate ? null : id);

  const handleClientDataChange = (e) => {
    const { name, value } = e.target;
    setClientData((prev) => ({ ...prev, [name]: value }));
  };

  const selectedAvailability = selectedDate
    ? availability.find((a) => a.id_available === selectedDate)
    : null;

  // Calcular montos con comisión del 3%
  const baseAmountColon = Number(service?.price || 0) * participants;
  console.log("this is the base column", baseAmountColon);
  const commission =baseAmountColon*0.07;
  const totalAmountColon = Math.round(baseAmountColon+commission);
  

  // Función para crear la reserva y enviar el código de verificación
  const createBookingAndSendVerification = async () => {
    if (!selectedAvailability) {
      alert(t.reservation.selectDateAlert);
      return;
    }
    if (!clientData.name || !clientData.email || !clientData.phone) {
      alert(t.reservation.completeDataAlert);
      return;
    }

    setIsPaying(true);

    const bookingData = {
      service_id: service.id_service,
      date_service: selectedAvailability.date_service,
      hour_start: selectedAvailability.hour_start,
      hour_end: selectedAvailability.hour_end,
      participants,
      total_amount_usd: totalAmountColon,
      client_name: clientData.name,
      client_email: clientData.email,
      client_phone: clientData.phone,
      client_country: clientData.country,
      id_operator: operator.id_operator,
      id_available: selectedAvailability.id_available,
      order_id: uuid()
    };

    try {
      const bookingResponse = await fetch(`${URL}/api/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingData),
      });
      
      const bookingResult = await bookingResponse.json();
      if (!bookingResponse.ok) throw new Error(bookingResult.message || t.reservation.bookingError);
      
      // Guardar el ID de la reserva y avanzar al paso de verificación
      setBookingId(bookingResult.booking_id);
      setStep(5); // Ir al paso de verificación
      
    } catch (err) {
      console.error("Error al crear la reserva:", err);
      alert(`${t.reservation.bookingError}: ${err.message}`);
    } finally {
      setIsPaying(false);
    }
  };

  // Función para verificar el código
  const verifyCode = async () => {
    if (!verificationCode) {
      setVerificationError(t.reservation.verificationCodeRequired);
      return;
    }

    setIsVerifying(true);
    setVerificationError("");

    try {
      const response = await fetch(`${URL}/api/booking/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          booking_id: bookingId,
          verification_code: verificationCode,
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || t.reservation.verificationFailed);
      }

      // Si la verificación es exitosa, proceder con el pago
      //await proceedToPayment();
    navigate("/PaymentServiceOnvo", {
      state: {
        booking: {
          id_booking: bookingId,
          service_name: service.name,
          total_amount_colon: totalAmountColon,
          id_operator: operator.id_operator
          //participants
        },
      },
      });
    } catch (err) {
      console.error("Error en verificación:", err);
      setVerificationError(err.message);
    } finally {
      setIsVerifying(false);
    }
  };


  // Función para reenviar el código de verificación
  const resendVerificationCode = async () => {
    try {
      const response = await fetch(`${URL}/api/booking/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_id: bookingId }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || t.reservation.resendFailed);
      }

      alert(t.reservation.codeResent);
    } catch (err) {
      console.error("Error al reenviar código:", err);
      alert(`${t.reservation.resendError}: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
              <div className="h-64 bg-gray-200 rounded w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t.reservation.serviceNotFound}</h3>
            <p className="text-gray-700">{t.reservation.serviceUnavailable}</p>
            <button onClick={() => navigate("/")} className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              {t.reservation.backToHome}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <nav className="flex mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <div className="flex items-center">
                  <a href="/" className="text-sm font-medium text-gray-500 hover:text-gray-700">{t.reservation.home}</a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <a href="/servicios" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">{t.reservation.services}</a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="CurrentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-700">{service.name}</span>
                </div>
              </li>
            </ol>
          </nav>

          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2">
              <div className="mb-8">
                <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-2">{service.name}</h1>
                <div className="flex items-center mb-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{service.category}</span>
                </div>
              </div>
                {service.coverImage && (
              <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
                <img 
                  src={service.coverImage} 
                  alt={service.name}
                  className="w-full h-64 md:h-80 object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

              <div className="bg-white shadow rounded-lg p-6 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{t.reservation.serviceDescription}</h2>
                <p className="text-gray-700 mb-6">{service.description}</p>
              </div>
             


              {operator && operator.Name && (
                <div className="bg-white shadow rounded-lg p-6 mb-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">{t.reservation.aboutOperator}</h2>
                  <div className="flex flex-col md:flex-row">
                    <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
                      <img src={operator.Picture} alt={`${operator.Name} ${operator.Last_name1}`} className="w-32 h-32 object-cover rounded-full border-4 border-white shadow" />
                    </div>
                    <div>
                      <div className="flex items-center mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{operator.Name} {operator.Last_name1} {operator.Last_name2}</h3>
                        {operator.Status === "active" && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{t.reservation.active}</span>
                        )}
                      </div>
                      {operator.Description && <p className="text-gray-700 mb-2">{operator.Description}</p>}
                      <div className="flex flex-wrap gap-4 mt-4">
                        <a href={`mailto:${operator.Email}`} className="inline-flex items-center text-sm text-gray-700 hover:text-gray-900"><FaEnvelope className="mr-2" /> {operator.Email}</a>
                        <a href={`tel:${operator.Phone}`} className="inline-flex items-center text-sm text-gray-700 hover:text-gray-900"><FaPhone className="mr-2" /> {operator.Phone}</a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white shadow rounded-lg sticky top-8 overflow-hidden">
                {step === 1 && (
                  <>
                    <div className="p-6 border-b border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900">{t.reservation.bookNow}</h3>
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">{t.reservation.pricePerPerson}</span>
                          <span className="font-bold text-gray-900">₡{service.price} CRC </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 border-b border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-4">{t.reservation.selectDate}</h4>
                      {availability.length === 0 ? (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-yellow-700">{t.reservation.noAvailability}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {availability.map((item) => (
                            <div
                              key={item.id_available}
                              className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedDate === item.id_available ? "border-green-500 bg-green-50" : "border-gray-200 hover:border-gray-300"}`}
                              onClick={() => handleDateSelect(item.id_available)}
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                  <FaCalendarAlt />
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-gray-900">
                                    {dateFromYMDLocal(item.date_service).toLocaleDateString(language === 'es' ? "es-ES" : "en-US", {
                                      weekday: "long",
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    })}
                                  </p>
                                  <div className="flex items-center text-sm text-gray-500">
                                  <FaClock className="mr-1" />
                                    {item.hour_start.split(":").slice(0, 2).join(":")} - {item.hour_end.split(":").slice(0, 2).join(":")}
                                  </div>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-gray-500">{service.max_capacity} {t.reservation.availableSlots}</div>
                            </div>
                          ))}
                        </div>
                  )}
                    </div>

                    {selectedDate && (
                      <div className="p-6">
                        <button onClick={() => setStep(2)} className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 rounded-md font-medium text-white">{t.reservation.continueBooking}</button>
                      </div>
                    )}
                  </>
                )}

                {step === 2 && (
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">{t.reservation.bookingDetails}</h3>

                    {selectedAvailability && (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">{t.reservation.selectedDate}:</h4>
                        <p>
                          {dateFromYMDLocal(selectedAvailability.date_service).toLocaleDateString(language === 'es' ? "es-ES" : "en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                        <p className="text-sm text-gray-600">
                          <FaClock className="inline mr-1" />
                          {selectedAvailability.hour_start} - {selectedAvailability.hour_end}
                        </p>
                      </div>
                    )}

                    <div className="mb-6">
                      <label htmlFor="participants" className="block text-sm font-medium text-gray-700 mb-2">{t.reservation.numberOfParticipants}</label>
                      <div className="flex items-center">
                        <button onClick={() => setParticipants((p) => Math.max(1, p - 1))} className="px-3 py-1 border border-gray-300 rounded-l-md bg-gray-50 hover:bg-gray-100">-</button>
                        <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-center w-16">{participants}</div>
                        <button onClick={() => setParticipants((p) => Math.min(service.max_capacity, p + 1))} className="px-3 py-1 border border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100">+</button>
                        <span className="ml-2 text-sm text-gray-500">{t.reservation.maximum}: {service.max_capacity}</span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4 mb-6">
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-700">{t.reservation.pricePerPerson}:</span>
                        <span className="text-gray-900">{service.price} CRC</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">{t.reservation.commission} (7%):</span>
                        <span className="text-gray-900">₡{(service.price * participants * 0.07).toFixed(2)} CRC</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg mt-2">
                        <span>{t.reservation.total}:</span>
                        <span>₡{(service.price * participants * 1.07).toFixed(2)} CRC</span>
                      </div>
                    </div>

                    <button onClick={() => setStep(3)} className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 rounded-md font-medium text-white mb-2">{t.reservation.continuePersonalData}</button>
                    <button onClick={() => setStep(1)} className="w-full py-2 px-4 bg-white hover:bg-gray-50 rounded-md font-medium text-gray-700 border border-gray-300">{t.reservation.goBack}</button>
                  </div>
                )}

                {step === 3 && (
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">{t.reservation.clientData}</h3>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{t.reservation.fullName}</label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaUser className="h-5 w-5 text-gray-400" />
                          </div>
                          <input type="text" name="name" id="name" required value={clientData.name} onChange={handleClientDataChange} className="focus:ring-green-500 focus:border-green-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 border" />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{t.reservation.email}</label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaEnvelope className="h-5 w-5 text-gray-400" />
                          </div>
                          <input type="email" name="email" id="email" required value={clientData.email} onChange={handleClientDataChange} className="focus:ring-green-500 focus:border-green-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 border" />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">{t.reservation.phone}</label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaPhone className="h-5 w-5 text-gray-400" />
                          </div>
                          <input type="tel" name="phone" id="phone" required value={clientData.phone} onChange={handleClientDataChange} className="focus:ring-green-500 focus:border-green-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 border" />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">{t.reservation.country}</label>
                        <select name="country" id="country" value={clientData.country} onChange={handleClientDataChange} className="focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 border">
                          <option value="Costa Rica">{t.reservation.costaRica}</option>
                          <option value="Estados Unidos">{t.reservation.usa}</option>
                          <option value="Canadá">{t.reservation.canada}</option>
                          <option value="España">{t.reservation.spain}</option>
                          <option value="Otro">{t.reservation.other}</option>
                        </select>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">{t.reservation.baseAmount}:</span>
                          <span className="text-gray-900">{baseAmountColon.toFixed(2)} CRC</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">{t.reservation.commission} (7%):</span>
                          <span className="text-gray-900">{commission.toFixed(2)} CRC</span>
                        </div>
                        <div className="flex justify-between items-center mb-4 font-bold">
                          <span className="text-gray-900">{t.reservation.totalReference}:</span>
                          <span className="text-gray-900">{totalAmountColon.toFixed(2)} CRC</span>
                        </div>

                        <button onClick={() => setStep(4)} className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 rounded-md font-medium text-white mb-2" disabled={!clientData.name || !clientData.email || !clientData.phone}>
                          {t.reservation.proceedToPayment}
                        </button>

                        <button onClick={() => setStep(2)} className="w-full py-2 px-4 bg-white hover:bg-gray-50 rounded-md font-medium text-gray-700 border border-gray-300">{t.reservation.goBack}</button>
                      </div>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">{t.reservation.paymentMethod}</h3>

                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <FaMoneyBillWave className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">{t.reservation.securePayment}</h3>
                          <p className="text-sm text-blue-700 mt-1">{t.reservation.paymentRedirect}</p>
                        </div>
                      </div>
                    </div>

                    {/* Currency */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.reservation.currency}</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setCurrency("CRC")} className={`py-2 rounded border ${currency === "CRC" ? "border-green-600 bg-green-50" : "border-gray-300 bg-white"}`}>{t.reservation.colones}</button>
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.reservation.method}</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2">
                          <input type="radio" name="paymentOption" value="CARD" checked={paymentOption === "CARD"} onChange={() => setPaymentOption("CARD")} />
                          <span>{t.reservation.card}</span>
                        </label>
                        
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="border rounded-lg p-4 mb-6">
                      <h4 className="font-medium text-gray-900 mb-3">{t.reservation.bookingSummary}</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">{t.reservation.service}:</span><span className="text-gray-900">{service.name}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">{t.reservation.date}:</span><span className="text-gray-900">{dateFromYMDLocal(selectedAvailability.date_service).toLocaleDateString(language === 'es' ? "es-ES" : "en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">{t.reservation.schedule}:</span><span className="text-gray-900">{selectedAvailability.hour_start} - {selectedAvailability.hour_end}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">{t.reservation.participants}:</span><span className="text-gray-900">{participants}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">{t.reservation.baseAmount}:</span><span className="text-gray-900">₡{baseAmountColon.toFixed(2)} CRC</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">{t.reservation.commission} (7%):</span><span className="text-gray-900">₡{commission.toFixed(2)} CRC</span></div>
                        <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
                          <span className="font-medium text-gray-900">{t.reservation.totalReference}:</span>
                          <span className="font-bold text-gray-900">₡{totalAmountColon.toFixed(2)} CRC</span>
                        </div>
                      </div>
                    </div>

                    <button onClick={createBookingAndSendVerification} className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 rounded-md font-medium text-white mb-2" disabled={isPaying}>
                      {isPaying ? t.reservation.creatingBooking : t.reservation.createBooking}
                    </button>

                    <button onClick={() => setStep(3)} className="w-full py-2 px-4 bg-white hover:bg-gray-50 rounded-md font-medium text-gray-700 border border-gray-300">{t.reservation.goBack}</button>
                  </div>
                )}

                {/* Nuevo paso: Verificación de código */}
                {step === 5 && (
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">{t.reservation.verifyEmail}</h3>

                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <FaKey className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">{t.reservation.verificationSent}</h3>
                          <p className="text-sm text-blue-700 mt-1">
                            {t.reservation.checkEmail} <strong>{clientData.email}</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-1">
                          {t.reservation.verificationCode}
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaKey className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="verificationCode"
                            id="verificationCode"
                            required
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            className="focus:ring-green-500 focus:border-green-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 border"
                            placeholder={t.reservation.enterCode}
                          />
                        </div>
                        {verificationError && (
                          <p className="mt-1 text-sm text-red-600">{verificationError}</p>
                        )}
                      </div>

                      <button
                        onClick={verifyCode}
                        className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 rounded-md font-medium text-white mb-2"
                        disabled={isVerifying}
                      >
                        {isVerifying ? t.reservation.verifying : t.reservation.verifyAndPay}
                      </button>

                      <button
                        onClick={resendVerificationCode}
                        className="w-full py-2 px-4 bg-white hover:bg-gray-50 rounded-md font-medium text-gray-700 border border-gray-300 mb-2"
                      >
                        {t.reservation.resendCode}
                      </button>

                      <button
                        onClick={() => setStep(4)}
                        className="w-full py-2 px-4 bg-white hover:bg-gray-50 rounded-md font-medium text-gray-700 border border-gray-300"
                      >
                        {t.reservation.goBack}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Reservation;